import { useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BrandsGrid from '../sections/BrandsGrid';
import { AntiMetalButton } from '../components/ui/anti-metal-button';
import './AboutPage.css';


const KEY_POINTS = [
  {
    num: '001',
    tag: '/Strategy',
    title: 'Strategy before execution',
    text: 'Every project starts with a clear brief. We align on goals, audience, and success metrics before a single pixel is designed.',
  },
  {
    num: '002',
    tag: '/Creative',
    title: 'Creative that converts',
    text: 'We don\'t separate aesthetics from performance. Our work is built to look exceptional and drive measurable results.',
  },
  {
    num: '003',
    tag: '/Process',
    title: 'Transparent process',
    text: 'You\'re never in the dark. Regular check-ins, shared timelines, and honest communication at every stage.',
  },
  {
    num: '004',
    tag: '/Scale',
    title: 'Built for scale',
    text: 'Whether you\'re launching or growing, our systems and deliverables are designed to evolve with your brand.',
  },
  {
    num: '005',
    tag: '/Deliver',
    title: 'One team, full spectrum',
    text: 'Strategy, design, development, and marketing — all under one roof. No handoff gaps, no misaligned vendors.',
  },
];
const FRAME_COUNT  = 25;
const FRAME_PREFIX = '/imagesequence/frame-';
const FRAME_PAD    = 3;
const FRAME_EXT    = '.png';
// ─────────────────────────────────────────────────────────────────────────────

function pad(n, len) { return String(n).padStart(len, '0'); }
function frameSrc(i) { return `${FRAME_PREFIX}${pad(i + 1, FRAME_PAD)}${FRAME_EXT}`; }

function ImageSequence() {
  const wrapRef    = useRef(null);
  const canvasRef  = useRef(null);
  const framesRef  = useRef([]);
  const loadedRef  = useRef(0);
  const currentRef = useRef(0);
  const rafRef     = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap   = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');

    // Resize canvas to match display size
    function resize() {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      drawFrame(currentRef.current);
    }

    function drawFrame(index) {
      const img = framesRef.current[index];
      if (!img || !img.complete) return;
      const cw = canvas.width, ch = canvas.height;
      const iw = img.naturalWidth, ih = img.naturalHeight;
      // Cover fit
      const scale = Math.max(cw / iw, ch / ih);
      const sw = iw * scale, sh = ih * scale;
      const sx = (cw - sw) / 2, sy = (ch - sh) / 2;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, sx, sy, sw, sh);
    }

    function onScroll() {
      const rect    = wrap.getBoundingClientRect();
      const total   = wrap.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const raw     = Math.min(1, scrolled / total);
      const target  = Math.round(raw * (FRAME_COUNT - 1));
      if (target !== currentRef.current) {
        currentRef.current = target;
        drawFrame(target);
      }
    }

    // Preload all frames
    framesRef.current = Array.from({ length: FRAME_COUNT }, (_, i) => {
      const img = new Image();
      img.src = frameSrc(i);
      img.onload = () => {
        loadedRef.current++;
        // Draw first frame as soon as it loads
        if (i === 0) { resize(); drawFrame(0); }
      };
      return img;
    });

    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    resize();
    onScroll();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="ap-seq-wrap" ref={wrapRef}>
      <div className="ap-seq-sticky">
        <canvas ref={canvasRef} className="ap-seq-canvas" />
        <div className="ap-seq-overlay" />

        {/* Text overlaid on the sequence */}
        <div className="ap-seq-content">
          <h1 className="ap-hero-heading ap-blur-reveal">
            Clarity Over Vanity.<br /><span style={{ color: '#ec2b28' }}>Results</span> Over Reports.
          </h1>
          <div className="ap-hero-body">
            <div className="ap-col">
              <p className="ap-blur-reveal">
                Aurelix Brands Private Limited is a consulting and growth enablement firm founded in 2025,
                built for founders and operators who are tired of agencies that optimise for impressions instead of income.
              </p>
              <p className="ap-blur-reveal">
                We connect your ads, funnels, sales data, and operations into one integrated system — so you always know
                what's working, what's wasting money, and what to do next.
              </p>
            </div>
            <div className="ap-col">
              <p className="ap-blur-reveal">
                Whether you need to scale paid media, fix your conversion funnel, or build analytics infrastructure
                from scratch, we work as an extension of your team until the numbers move.
              </p>
              <p className="ap-blur-reveal">
                Data First — every decision backed by dashboards, not gut feel. Systems, Not Sprints — automations
                that keep delivering after we leave. Measurable Growth — CAC, ROAS, LTV — the metrics that actually matter.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AboutPage() {
  const kpSectionRef = useRef(null);
  const progressBarRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const section = kpSectionRef.current;
    const bar = progressBarRef.current;
    if (!section || !bar) return;

    const TRIGGER = 0.62; // item activates when its top crosses 62% of viewport

    function onScroll() {
      const winH = window.innerHeight;
      let activeIndex = -1;

      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const entered = rect.top < winH * TRIGGER;

        // Reveal: slide up + fade in + blur reveal
        if (entered) {
          el.style.opacity   = '1';
          el.style.transform = 'translateY(0)';
          el.style.filter    = 'blur(0px)';
          activeIndex = i;
        } else {
          el.style.opacity   = '0';
          el.style.transform = 'translateY(40px)';
          el.style.filter    = 'blur(8px)';
        }

        // Active class for color changes
        el.classList.toggle('ap-kp-item--active', entered);
      });

      // Progress bar fills to match how many items have been revealed
      const total = itemRefs.current.length;
      const filled = activeIndex >= 0 ? (activeIndex + 1) / total : 0;
      bar.style.transform = `scaleY(${filled})`;
    }

    // Set initial hidden state for all items
    itemRefs.current.forEach(el => {
      if (!el) return;
      el.style.opacity   = '0';
      el.style.transform = 'translateY(40px)';
      el.style.filter    = 'blur(8px)';
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease, filter 0.8s ease';
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Blur reveal effect for all sections
  useEffect(() => {
    const blurElements = document.querySelectorAll('.ap-blur-reveal');
    
    function onScroll() {
      const winH = window.innerHeight;
      
      blurElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const entered = rect.top < winH * 0.75;
        
        if (entered) {
          el.style.opacity = '1';
          el.style.filter = 'blur(0px)';
          el.style.transform = 'translateY(0)';
        }
      });
    }
    
    // Set initial state
    blurElements.forEach(el => {
      el.style.opacity = '0';
      el.style.filter = 'blur(8px)';
      el.style.transform = 'translateY(30px)';
      el.style.transition = 'opacity 0.8s ease, filter 0.8s ease, transform 0.8s ease';
    });
    
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Navbar />
      <main className="ap-main">
        <ImageSequence />

        {/* ── Key points: timeline left, content right ── */}
        <section className="ap-kp-section" ref={kpSectionRef}>
          <div className="ap-kp-inner">

            {/* LEFT — sticky heading + stats */}
            <div className="ap-kp-left">
              <div className="ap-kp-left-content">
                <p className="ap-kp-label ap-blur-reveal">How we work</p>
                <h2 className="ap-kp-heading ap-blur-reveal">
                  What <span style={{ color: '#ec2b28' }}>Aurelix</span><br />delivers
                </h2>
                <p className="ap-kp-desc ap-blur-reveal">
                  A structured, creative-led model applied across every project.
                  Connecting the right people, closing decisions on time, and
                  executing with precision.
                </p>
                <div className="ap-kp-stats ap-blur-reveal">
                  <div className="ap-kp-stat">
                    <span className="ap-kp-stat-num">$32M+</span>
                    <span className="ap-kp-stat-label">USD in incremental revenue</span>
                  </div>
                  <div className="ap-kp-stat">
                    <span className="ap-kp-stat-num">20+</span>
                    <span className="ap-kp-stat-label">Brands</span>
                  </div>
                  <div className="ap-kp-stat">
                    <span className="ap-kp-stat-num">7+</span>
                    <span className="ap-kp-stat-label">Years</span>
                  </div>
                  <div className="ap-kp-stat">
                    <span className="ap-kp-stat-num">95%</span>
                    <span className="ap-kp-stat-label">Client retention rate</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — timeline + items */}
            <div className="ap-kp-right">

              {/* Vertical timeline track */}
              <div className="ap-kp-timeline">
                <div className="ap-kp-timeline-track">
                  <div className="ap-kp-timeline-fill" ref={progressBarRef} />
                </div>

                {KEY_POINTS.map((kp, i) => (
                  <div
                    className="ap-kp-item"
                    key={kp.num}
                    ref={el => (itemRefs.current[i] = el)}
                  >
                    {/* Marker on the line */}
                    <div className="ap-kp-marker">
                      <span className="ap-kp-marker-dot" />
                      <span className="ap-kp-marker-num">/{kp.num}/</span>
                    </div>

                    {/* Content */}
                    <div className="ap-kp-content">
                      <span className="ap-kp-tag">{kp.tag}</span>
                      <h3 className="ap-kp-title">{kp.title}</h3>
                      <p className="ap-kp-text">{kp.text}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>

          </div>
        </section>

        {/* ── Clients Section ── */}
        <BrandsGrid />

        {/* ── CTA Banner Section ── */}
        <section className="ap-cta-section">
          <div className="ap-cta-banner ap-blur-reveal">
            <div className="ap-cta-content">
              <h2 className="ap-cta-heading">
                let's build<br />
                something<br />
                <span className="ap-cta-italic">iconic</span> together.
              </h2>
              <AntiMetalButton as="a" href="/contact">
                Work With Us
              </AntiMetalButton>
            </div>
          </div>
        </section>

        {/* ── Approach Section ── */}
        <section className="ap-approach-section">
          <div className="ap-approach-inner">
            <div className="ap-approach-left">
              <h3 className="ap-approach-title ap-blur-reveal">Approach</h3>
            </div>
            <div className="ap-approach-right">
              <p className="ap-approach-text ap-blur-reveal">
                We approach design as a structured discipline, not an afterthought.<br />
                Every project is built around clarity, intent, and execution that holds up over time.
              </p>
              <div className="ap-approach-details">
                <p className="ap-approach-detail ap-blur-reveal">
                  Before anything is created, we focus on alignment.<br />
                  What needs to be built, how it should function, and what outcome it must deliver.
                </p>
                <p className="ap-approach-detail ap-blur-reveal">
                  With that in place, we execute with precision, keeping things simple, consistent, and effective.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Meet The Minds Section ── */}
        <section className="ap-founders-section">
          <div className="ap-founders-inner">
            <div className="ap-founders-header">
              <p className="ap-founders-label ap-blur-reveal">The Minds Behind Aurelix</p>
              <h2 className="ap-founders-heading ap-blur-reveal">
                Built on <span style={{ color: '#ec2b28' }}>data</span>,<br />
                driven by <span style={{ color: '#ec2b28' }}>results</span>.
              </h2>
            </div>

            <div className="ap-founders-list">

              {/* Top row: 2 founder cards + quote all in one row */}
              <div className="ap-founders-cards-row">

                {/* Abhijeet */}
                <div className="ap-founder-row ap-blur-reveal">
                  <div className="ap-founder-avatar">
                    <span className="ap-founder-initials">ASJ</span>
                  </div>
                  <div className="ap-founder-info">
                    <div className="ap-founder-top">
                      <div>
                        <h3 className="ap-founder-name">Abhijeet Singh Jain</h3>
                        <p className="ap-founder-role">Co-Founder · Growth & Analytics</p>
                      </div>
                      <a href="https://linkedin.com/in/abhijeet-singh-jain" target="_blank" rel="noopener noreferrer" className="ap-founder-linkedin">LinkedIn ↗</a>
                    </div>
                    <p className="ap-founder-bio">7+ years across India and the US. 20+ brands, Fortune 100 to D2C startups. Rs.2Cr+ in ad budgets. MS in Business Analytics, UT Dallas.</p>
                    <div className="ap-founder-skills">
                      {['Meta Ads', 'Google Ads', 'SQL', 'Tableau', 'GA4', 'CRO', 'AI Automation'].map(s => (
                        <span key={s} className="ap-founder-skill">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Ishita */}
                <div className="ap-founder-row ap-blur-reveal">
                  <div className="ap-founder-avatar">
                    <img src="/foundersimage/Ishita-Jain-2.jpeg" alt="Ishita Jain" className="ap-founder-photo" />
                  </div>
                  <div className="ap-founder-info">
                    <div className="ap-founder-top">
                      <div>
                        <h3 className="ap-founder-name">Ishita Jain</h3>
                        <p className="ap-founder-role">Co-Founder · UI/UX & CRO</p>
                      </div>
                      <a href="https://linkedin.com/in/ishitajain20" target="_blank" rel="noopener noreferrer" className="ap-founder-linkedin">LinkedIn ↗</a>
                    </div>
                    <p className="ap-founder-bio">3+ years in D2C UI/UX and web dev. Built two D2C startups across US and India. Heatmaps, A/B tests, session data. NMIMS graduate.</p>
                    <div className="ap-founder-skills">
                      {['UI/UX Design', 'Shopify', 'Figma', 'CRO', 'A/B Testing', 'Web Dev'].map(s => (
                        <span key={s} className="ap-founder-skill">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Quote — same row */}
                <div className="ap-founders-quote ap-blur-reveal">
                  <span className="ap-quote-mark">"</span>
                  <p className="ap-quote-text">
                    We built Aurelix because brands deserve an agency that connects <span className="scroll-hl" style={{ '--hl-d': '0s' }}>ads, data, and operations</span> into <span className="scroll-hl" style={{ '--hl-d': '0.2s' }}>one system</span> — focused on one thing: <span className="scroll-hl" style={{ '--hl-d': '0.4s' }}>revenue</span>.
                  </p>
                  <div className="ap-quote-attribution">
                    <span className="ap-quote-line" />
                    <span className="ap-quote-authors">Abhijeet & Ishita, Co-Founders</span>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </section>

        {/* ── What Drives Us Section ── */}
        <section className="ap-drives-section">
          <div className="ap-drives-inner">
            <div className="ap-drives-left">
              <h3 className="ap-drives-title ap-blur-reveal">What Drives Us</h3>
            </div>
            <div className="ap-drives-right">
              <p className="ap-drives-text ap-blur-reveal">
                We are driven by clarity, quality, and disciplined execution.<br />
                Every project is an opportunity to deliver work that is structured, consistent, and built to last.
              </p>
              <div className="ap-drives-details">
                <p className="ap-drives-detail ap-blur-reveal">
                  Our focus remains the same across every engagement.
                </p>
                <p className="ap-drives-detail ap-blur-reveal">
                  Do the work right, maintain high standards, and avoid unnecessary complexity.
                </p>
                <p className="ap-drives-detail ap-blur-reveal">
                  That consistency is what defines the outcomes we deliver.
                </p>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
